/**
 * Theme mode vocabulary and resolution.
 *
 * ENGINEERING INFRASTRUCTURE ONLY. FE-1 Stage Work Item §4.1 D authorizes "theme
 * mechanism ... light/dark mechanism" and approves no visual design, brand or
 * aesthetic. This module decides which palette is active; it does not decide what
 * the palette looks like.
 *
 * The resolution function is pure so that the light/dark mechanism is checkable
 * without a browser, and so nothing here touches storage, cookies or the network.
 */

/** What the operator asked for. `SYSTEM` defers to the operating system. */
export type ThemeMode = 'LIGHT' | 'DARK' | 'SYSTEM';

/** What is actually applied. `SYSTEM` has been resolved away. */
export type ResolvedTheme = 'LIGHT' | 'DARK';

export const DEFAULT_THEME_MODE: ThemeMode = 'SYSTEM';

export const THEME_MODES = Object.freeze([
  'LIGHT',
  'DARK',
  'SYSTEM',
] as const satisfies readonly ThemeMode[]);

/**
 * Resolve a requested mode against the system preference.
 *
 * An explicit `LIGHT` or `DARK` always wins: the mechanism must not second-guess
 * an operator's choice. Only `SYSTEM` consults `systemPrefersDark`.
 */
export function resolveThemeMode(mode: ThemeMode, systemPrefersDark: boolean): ResolvedTheme {
  switch (mode) {
    case 'LIGHT':
      return 'LIGHT';
    case 'DARK':
      return 'DARK';
    case 'SYSTEM':
      return systemPrefersDark ? 'DARK' : 'LIGHT';
  }
}
